// import { useStorage } from '@vueuse/core'

export default defineNuxtPlugin(async (nuxtApp) => {
  const { confGlobal, confNavigation } = useConfig()
  const { requestHeaders } = useApi()
  const { useCustomCookie } = useCustomTools()
  const { expiredModal, setReferral, clearCustomerData } = useCustomer()
  const { isProcessBlockPage } = useHelpers()
  const toast = useToast()

  const entryReferrer = useCustomCookie('entryReferrer', 60 * 60 * 24 * 30)

  /**
  * Customer Class
  */
  class Customer {
    constructor () {
      this.isMember = useCustomCookie('isMember')
      this.memberToken = useCustomCookie('memberToken')
      this.referralToken = useCustomCookie('referralToken')
      this.user = useState('customer_user', () => false) // useStorage('customer_user', {})
      this.statistics = useState('customer_statistics', () => false)
      this.customerRoutes = []
      this.settingRoutes = []
      this.supportRoutes = []

      this.isAdminPreview = useCustomCookie('isAdminPreview')
      this.adminToken = useCustomCookie('adminToken')
      this.customerUuid = useCustomCookie('customerUuid')
    }

    /**
    * Logout
    */
    logout = (redirect, isClearCustomerData = true) => {
      /**
      * Delete the previous user's data
      */
      if (isClearCustomerData) {
        clearCustomerData()
      }

      /**
      * Clear Customer Info
      */
      this.isMember.value = false
      this.memberToken.value = false
      this.referralToken.value = false
      this.user.value = false
      this.statistics = {}
      this.customerRoutes = []
      this.settingRoutes = []
      this.supportRoutes = []

      this.isAdminPreview.value = false
      this.adminToken.value = false
      this.customerUuid.value = false

      useCustomCookie('memberToken').value = false

      if (redirect) {
        navigateTo('/guest/signin')
      }
    }

    /**
    * Auth Admin Preview
    */
    adminAuth = async (params) => {
      this.adminToken.value = params.adminToken
      this.customerUuid.value = params.customerUuid
      this.isAdminPreview.value = true

      clearCustomerData()

      await this.getMember().then((res) => {
        if (!res) {
          this.adminToken.value = false
          this.customerUuid.value = false
          this.isAdminPreview.value = false
        }

        reloadNuxtApp({
          path: '/customer',
          ttl: 1000
        })
      })
    }

    /**
    * Logout Admin Preview
    */
    adminPreviewLogout = async () => {
      this.isAdminPreview.value = false
      this.adminToken.value = false
      this.customerUuid.value = false

      clearCustomerData()

      await this.getMember()

      reloadNuxtApp({
        path: useRouter().currentRoute.value.fullPath,
        ttl: 1000
      })
    }

    /**
    * Set Customer Routes
    * is isCheck === true - only authorization check (check token)
    */
    setCustomerRoutes = () => {
      this.customerRoutes = confNavigation.customer.map((n) => {
        if (n.name === 'team-dashboard') {
          n.enable = !!(this.user.value && this.user.value?.company_id && this.user.value?.hasCompanyDashboard)
        }

        // if (n.name === 'campaigns') {
        //   n.enable = !!(this.user.value && this.user.value?.company_id && this.user.value?.can_set_campaign)
        // }

        // if (n.name === 'redeem') {
        //   n.enable = !!(this.user.value && this.user.value?.company_id && this.user.value?.can_set_campaign)
        // }

        if (n.name === 'orders-external') {
          n.enable = !!(this.user.value && (parseInt(this.user.value?.can_use_external_api) > 0))
        }

        if (n.name === 'thank-you-notes') {
          n.notification = this.statistics?.value?.statistics?.thank_you_notes
            ? (this.statistics?.value?.statistics?.thank_you_notes?.new != 0) ? this.statistics?.value?.statistics?.thank_you_notes?.new + ' / ' + this.statistics?.value?.statistics?.thank_you_notes?.total : ''
            : false
        }

        return n
      })
    }

    /**
    * Set Setting Routes
    * is isCheck === true - only authorization check (check token)
    */
    setSettingRoutes = () => {
      this.settingRoutes = confNavigation.setting.map((n) => {
        if (n.name === 'user-management') {
          n.enable = !!(this.user.value && this.user.value?.company_id && this.user.value?.user_manage_level)
        }
        
        return n
      })
    }

    /**
    * Set Support Routes
    * is isCheck === true - only authorization check (check token)
    */
    setSupportRoutes = () => {
      this.supportRoutes = confNavigation.support.map((n) => {
        return n
      })
    }

    /**
    * Get Statistics
    */
    getStatistics = async () => {
      return await new Promise((resolve) => {
        try {
          const headers = {
            Authorization: this.memberToken.value || ''
          }

          if (typeof this.adminToken.value === 'string' && typeof this.customerUuid.value === 'string') {
            headers['Admin-Token'] = this.adminToken.value
            headers['Customer-Uuid'] =  this.customerUuid.value
          }

          if (!this.memberToken.value && !this.adminToken.value) {
            resolve(false)
            return false
          }

          return $fetch(confGlobal.apiUrl + '/customers/statistics', {
            method: 'GET',
            headers: requestHeaders(headers)
          }).then(async (res) => {
            const newData = await res

            this.statistics.value = newData?.data

            this.setCustomerRoutes()
            this.setSettingRoutes()
            this.setSupportRoutes()

            /*
            * Return
            */
            resolve(newData)

            return newData
          }).catch(async (err) => {
            if (err.data && err?.data?.error) {
              console.log(err.data)
            }
            
            resolve(false)
            return false
          })
        } catch (err) {
          resolve(err)
          console.log(err)
        }
      })
    }

    /**
    * Get Customer
    * is isCheck === true - only authorization check (check token)
    */
    getMember = async (isCheck) => {
      return await new Promise((resolve) => {
        try {
          if (!this.adminToken.value) {
            this.isAdminPreview.value = false
            this.adminToken.value = false
            this.customerUuid.value = false

            if (!this.memberToken.value) {
              this.logout(false, false)

              /**
              * Check session. If Session Expired, show entry modal
              */
              if (this.isMember.value && isCheck) {
                expiredModal()
              }

              resolve(false)

              return false
            }
          }

          const headers = {
            Authorization: this.memberToken.value || ''
          }

          if (typeof this.adminToken.value === 'string' && typeof this.customerUuid.value === 'string') {
            headers['Admin-Token'] = this.adminToken.value
            headers['Customer-Uuid'] =  this.customerUuid.value
          }

          return $fetch(confGlobal.apiUrl + `/me?timestamp=${new Date().getTime()}`, {
            method: 'GET',
            headers: requestHeaders(headers)
          }).then(async (res) => {
            const newData = await res

            this.user.value = newData
            this.isMember.value = true

            /**
            * Reset Blocking Page
            */
            if (!this.user?.value?.checkout_is_in_progress) {
              isProcessBlockPage.value.checkout = false

              toast.removeGroup('important')
            }

            /**
            * Set referral
            */
            if (this.user.value && this.referralToken.value) {
              setReferral(this.referralToken.value)
            }

            this.setCustomerRoutes()
            this.setSettingRoutes()
            this.setSupportRoutes()

            /*
            * Get Statistics
            */
            this.getStatistics()

            /*
            * Return
            */
            resolve(newData)

            return newData
          }).catch(async (err) => {
            if (err.data && err?.data?.error) {
              this.isAdminPreview.value = false
              this.adminToken.value = false
              this.customerUuid.value = false

              if (err?.data?.error?.code === 401) {
                /**
                * Reset Blocking Page
                */
                if (!this.user?.value?.checkout_is_in_progress) {
                  isProcessBlockPage.value.checkout = false
                  toast.removeGroup('important')
                }

                /**
                * If Member Token Not Correctly, clear
                */
                this.logout()

                /**
                * Check session. If Session Expired, show entry modal
                */
                if (this.isMember.value && isCheck) {
                  expiredModal()
                }
              }
            }
            
            resolve(false)
            return false
          })
        } catch (err) {
          resolve(err)
          console.log(err)
        }
      })
    }
  }

  /**
  * Get Customer Class
  */
  const member = new Customer()
  if (!useRouter().currentRoute.value?.name?.includes('customer-admin-auth')) {
    await member.getMember()
  } else {
    if (useRouter().currentRoute.value.query['Admin-Token'] && useRouter().currentRoute.value.query['Customer-Uuid']) {
      await member.adminAuth({
        adminToken: useRouter().currentRoute.value.query['Admin-Token'],
        customerUuid: useRouter().currentRoute.value.query['Customer-Uuid']
      })
    }
  }
  
  nuxtApp.provide('member', member)

  /**
  * Update Customer Data
  */
  watch(useInterval(30000), () => {
    member.getMember(true, member.memberToken.value)
  }, { deep: true })

  /**
  * Check Page Allow
  */
  watch(member.isMember, (newIsMember, oldIsMember) => {
    if (newIsMember !== oldIsMember) {
      if (useRouter().currentRoute.value.path.includes('/customer')) {
        if (!newIsMember) {
          entryReferrer.value = useRouter().currentRoute.value.path
          return navigateTo('/guest/signin')
        } else if (useRouter().currentRoute.value.path !== '/customer/projects') {
          return navigateTo('/customer/projects')
        }
      } else if (useRouter().currentRoute.value.path.includes('/guest')) {
        if (entryReferrer.value && !entryReferrer.value.includes('/customer/information')) {
          navigateTo(entryReferrer.value)
          entryReferrer.value = false
        } else {
          return navigateTo('/customer/projects')
        }
      }
    }
  }, { deep: true })
})
